import styled from 'styled-components/macro';
import detailsImage from '../../../images/details.jpg';
import '../../../fonts/Univers-light-normal.ttf';
import { colourPrimaryDarkPurple } from '../../../styling/colours';
import { DescriptionBlock } from './DescriptionBlock';
import { RevisionBox } from './RevisionBox';
import { AddressBox } from './AddressBox';
import { JobReference } from './JobReference';
import { PageDetailsBox } from './PageDetailsBox';
import { PdfDetailResponse } from '../PdfDataResponse';
import { Table, TBody, Tr, Td } from '../../../shared/Table';
import { spacing02 } from '../../../styling/spacing';

export type SidebarDetails = {
  pageName: string;
  pdfDetail: PdfDetailResponse;
  pageNumber: number;
  totalNumberOfPages: number;
  type: 'Boiler' | 'HeatPump' | 'Manifold';
};

type Props = SidebarDetails & {
  className?: string;
};

const getTopBox = (type: Props['type']): (() => JSX.Element) => {
  switch (type) {
    case 'Boiler':
      return BoilerTopBox;
    case 'HeatPump':
      return HeatPumpTopBox;
    case 'Manifold':
      return ManifoldDiagramTopBox;
  }
};

export const Sidebar = ({
  pageName,
  pdfDetail: { address, qrNumber, currentRevision, revisions, designerInitials, drawerInitials },
  pageNumber,
  totalNumberOfPages,
  type,
  className,
}: Props) => {
  const TopBox = getTopBox(type);

  return (
    <SidebarContainer className={className}>
      <TopBox />
      <BottomBox>
        <RevisionBox revisions={revisions} />
        <AddressBox address={address} />
        <PageDetailsBox
          pageName={pageName}
          date={new Date()}
          pageNumber={pageNumber}
          totalPages={totalNumberOfPages}
          designerInitials={designerInitials}
          drawerInitials={drawerInitials}
        />
        <ImageContainer>
          <ImageLogo src={detailsImage} alt="Nu Heat logo and address" />
        </ImageContainer>
        <JobReference reference={qrNumber} currentRevision={currentRevision} />
      </BottomBox>
    </SidebarContainer>
  );
};

const ImageContainer = styled.div`
  width: 100%;
  border: 1px solid black;
`;

const ImageLogo = styled.img`
  padding: 8px;
  width: 338px;
`;

const LegendTable = () => {
  return (
    <StyledTable>
      <TBody>
        <Tr>
          <Td>XX l/min</Td>
          <Td>Required Flow Rate through Pipe Section (l/min)</Td>
        </Tr>
      </TBody>
    </StyledTable>
  );
};

const StyledTable = styled(Table)`
  border-collapse: collapse;

  td:first-child {
    width: 60px;
  }

  td {
    white-space: pre-wrap;
    border: 1px solid black;
  }

  margin-bottom: ${spacing02};
`;

const BoilerTopBox = () => {
  return (
    <TopBox>
      <Title>Notes</Title>
      <DescriptionBlock>
        These drawings form part of the comprehensive Handover Pack and should be read in conjunction with the
        Installation Manuals and Data Sheets also supplied.
      </DescriptionBlock>
      <DescriptionBlock title="Boiler systems">
        Refer to Plumbing Manual for integration of underfloor heating with boiler, cylinder, radiator circuit etc. (as
        applicable). Manufacturer's guidance should always be followed.
      </DescriptionBlock>
      <DescriptionBlock title="Manifold dimensions">
        Dimensions stated include suitable access for pipe connections beside and below manifold.
      </DescriptionBlock>
      <DescriptionBlock title="Legend">
        The system layout shown opposite has been produced based on information supplied. Pipe distances shown have been
        estimated based on component locations, and pipe/pump sizes calculated accordingly. Refer to table below for
        key:
      </DescriptionBlock>
      <LegendTable />
      <DescriptionBlock title="Boiler sizing">
        The estimated boiler load for this system is shown opposite. It includes a suitable response factor but an
        additional allowance will be required for domestic hot water production and any areas of the property not served
        by this Nu-Heat underfloor heating system.
      </DescriptionBlock>
      <DescriptionBlock title="Expansion vessel sizing">
        A filling loop and expansion vessel (not supplied)will be required. The sizing information shown opposite is
        based on the water contained within this Nu-Heat underfloor heating system and pipework/components shown on this
        page.Additional expansion will be required for any parts of the system not shown here (eg. additional radiator
        circuits etc.). The sizing of this additional expansion is the responsibility of others.
      </DescriptionBlock>
    </TopBox>
  );
};

const HeatPumpTopBox = () => {
  return (
    <TopBox>
      <Title>Notes</Title>
      <DescriptionBlock>
        These drawings form part of the comprehensive Handover Pack and should be read in conjunction with the
        Installation Manuals and Data Sheets also supplied.
      </DescriptionBlock>
      <DescriptionBlock title="Nu-Heat supplied heat pump systems">
        Refer to Docking Drawings for heat pump,cylinder and buffer tank installation information.
      </DescriptionBlock>
      <DescriptionBlock title="Manifold dimensions">
        Dimensions stated include suitable access for pipe connections beside and below.
      </DescriptionBlock>
      <DescriptionBlock title="Legend">
        The system layout shown opposite has been produced based on information supplied. Pipe distances shown have been
        estimated based on component locations, and pipe/pump sizes calculated accordingly. Refer to table below for
        key:
      </DescriptionBlock>
      <LegendTable />
    </TopBox>
  );
};

const ManifoldDiagramTopBox = () => {
  return (
    <TopBox>
      <Title>Notes</Title>
      <DescriptionBlock>
        These drawings form part of the comprehensive Handover Pack and should be read in conjunction with the
        Installation Manuals and Data Sheets also supplied.
      </DescriptionBlock>
      <DescriptionBlock title="Towel rails">
        Nu-Heat advise that heated towel rails should be used in each bathroom, en suite and cloakroom to maintain
        comfort levels.
      </DescriptionBlock>
      <DescriptionBlock title="Electrical installation">
        For electrical installation, set up & commissioning information refer to supplied wiring diagrams. Installation
        must be carried out by a Competent Person or failing that, the local building control authority must be notified
        of the proposed work before commencement and the completed installation must be inspected by a Competent Person.
      </DescriptionBlock>
      <DescriptionBlock title="Thermostat positioning">
        Thermostats (or remote air sensors when specified) should be positioned at light switch height, away from
        additional heat sources and out of direct sunlight.
      </DescriptionBlock>
      <DescriptionBlock title="Floor coverings">
        Certain floor coverings require the use of a floor sensor to ensure a maximum floor surface temperature is not
        exceeded. It is the responsibility of the customer to advise of any specific floor temperature limits associated
        with your chosen floor coverings. Failure to install floor sensors in accordance with the supplied manual may
        invalidate warranties provided by your floor covering suppliers or have an adverse impact on performance. If
        additional floor sensors are required then the customer must advise Nu-Heat prior to installation. Where
        thermostats are sourced from an alternative supplier then appropriate floor sensors should be obtained from
        them.
      </DescriptionBlock>
    </TopBox>
  );
};

const BottomBox = styled.div`
  padding-left: 8px;
  padding-right: 8px;
  > * {
    margin-bottom: 8px;
  }
`;
const TopBox = styled.div`
  padding: 8px;
`;

const SidebarContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  padding: 4px;
  border-left: 1px solid black;

  @font-face {
    font-family: Univers, Helvetica, sans-serif;
    src: local(Univers), url('../../../fonts/Univers-light-normal.ttf') format('ttf');
  }
  font-family: Helvetica, sans-serif;

  table {
    th,
    td {
      font-family: Helvetica, sans-serif;
    }
  }
`;

const Title = styled.h1`
  font-size: 24px;
  margin-bottom: 0;
  background-color: ${colourPrimaryDarkPurple};
  color: white;
  text-align: center;
`;
